import axios, {type AxiosRequestConfig} from "axios";
import log from "@/log";
import config from "@/config";
import type {
  AuthReply, DbDoc, DocReply, GenericReply,
  ListReply, ApiConfig, SimpleListReply, DashboardReply, StatsReply,
} from "@/models";



class Api {
  config: ApiConfig
  constructor(conf:ApiConfig) {
    this.config = conf
  }

  private get baseUrl():String {
    return this.config.apiServer
  }

  private get apiPath():String {
    return 'api'
  }

  private handleError( error:any ) : GenericReply {
    log.d("api.handleError", error)
    if (error.response) {
      // Request made and server responded
      log.d(error.response.data);
      log.d(error.response.status);
      log.d(error.response.headers);
      const message = error.response.data ? error.response.data.message : error.response.data
      return {success: false, status: error.response.status, message};
    } else if (error.request) {
      // The request was made but no response was received
      log.d(error.request);
      return {success: false, status: 500, message: 'errore di connessione al server'};
    } else {
      // Something happened in setting up the request that triggered an Error
      log.d(error.message)
      return {success: false, status: 500, message: 'errore di connessione al server'};
    }
  }


  private handleReply( res:any ) {
    res.status = 200;
    return res
  }


  async login(email:string, password:string,) : Promise<AuthReply> {
    try {
      let res = await axios.post(`${this.baseUrl}/login`, {email, password})
      return this.handleReply(res.data)
    } catch (e) {
      return this.handleError(e)
    }
  }
  async heartbeat() : Promise<AuthReply> {
    const token = localStorage.token;
    try {
      let res = await axios.get(`${this.baseUrl}/heartbeat`, {headers: {'x-token': `${token}`}})
      return this.handleReply(res.data)
    } catch (e) {
      return this.handleError(e)
    }
  }


  async list<Type extends DbDoc>(collection:String, query:any, limit:number, page:number, sort?:string|null|undefined ):Promise<ListReply<Type>> {
    const token = localStorage.token;
    page = Math.max(page, 0);
    let url = `${this.baseUrl}/${this.apiPath}/${collection}?limit=${limit}&page=${page}`;
    for (const k in query) {
      if (query.hasOwnProperty(k) && query[k] !== undefined && query[k] !== null)
        url += "&" + k + "=" + encodeURIComponent(query[k]);
    }



    if (sort) {
        let order = 'ASC'
        if(sort[0] === '-') {
            order = 'DESC'
            sort = sort.slice(1)
        } else if(sort[0] === '+') {
            sort = sort.slice(1)
        }


      url += `&sort=${encodeURIComponent(sort)}&order=${order}`;
    }
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.get(url, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }
  async listNoPagination<Type extends DbDoc>(path:String, query?:any):Promise<SimpleListReply<Type>> {
    const token = localStorage.token;

    let url = `${this.baseUrl}/${this.apiPath}/${path}`;
    if(query) {
      for (const k in query) {
        if (query.hasOwnProperty(k) && query[k] !== undefined && query[k] !== null)
          url += "&" + k + "=" + encodeURIComponent(query[k]);
      }
    }
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.get(url, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }

  async get<Type>(collection:String, id?:string ):Promise<DocReply<Type>> {
    const token = localStorage.token;
    const url = id ? `${this.baseUrl}/${this.apiPath}/${collection}/${id}` : `${this.baseUrl}/api/${collection}`;
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.get(url, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }
  async getDashboard():Promise<DashboardReply> {
    const token = localStorage.token;
    const url = `${this.baseUrl}/${this.apiPath}/dashboard`;
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.get(url, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }
  async getSvc<Type extends DbDoc>(path:String):Promise<DocReply<Type>> {
    const token = localStorage.token;
    const url = `${this.baseUrl}/${path}`;
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.get(url, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }


  async post<Type extends DbDoc>(path:String, data:Type, query?:any ):Promise<DocReply<Type>> {
    const token = localStorage.token;
    let url = `${this.baseUrl}/${this.apiPath}/${path}`;
    if(query) {
      let components:any[] = []
      for (const k in query) {
        if (query.hasOwnProperty(k) && query[k] !== undefined && query[k] !== null)
          components.push( k + "=" + encodeURIComponent(query[k]) );
      }
      if(components.length) {
        url += `?${components.join('&')}`
      }
    }
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.post(url, data, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }

  async postAny(path:String, data:any, query?:any ):Promise<DocReply<any>> {
    const token = localStorage.token;
    let url = `${this.baseUrl}/${this.apiPath}/${path}`;
    if(query) {
      let components:any[] = []
      for (const k in query) {
        if (query.hasOwnProperty(k) && query[k] !== undefined && query[k] !== null)
          components.push( k + "=" + encodeURIComponent(query[k]) );
      }
      if(components.length) {
        url += `?${components.join('&')}`
      }
    }
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.post(url, data, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }
  async postSvc<Type extends DbDoc>(path:String, data:Type, query?:any ):Promise<DocReply<Type>> {
    const token = localStorage.token;
    let url = `${this.baseUrl}/${path}`;
    if(query) {
      let components:any[] = []
      for (const k in query) {
        if (query.hasOwnProperty(k) && query[k] !== undefined && query[k] !== null)
          components.push( k + "=" + encodeURIComponent(query[k]) );
      }
      if(components.length) {
        url += `?${components.join('&')}`
      }
    }
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.post(url, data, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }
  async upload<Type extends DbDoc>(path:String, data:FormData):Promise<DocReply<Type>> {
    const token = localStorage.token;
    let url = `${this.baseUrl}/${this.apiPath}/${path}`;
    const config = token ? {
      headers: {'x-token': `${token}`, 'Content-Type': 'multipart/form-data'},
    } : {
      headers: {'Content-Type': 'multipart/form-data'}
    };
    try {
      let res = await axios.post(url, data, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }

  async put<Type extends DbDoc>(path:String, data:Type, query?:any, noId?:boolean ):Promise<DocReply<Type>> {
    const token = localStorage.token;
    let url = noId ? `${this.baseUrl}/${this.apiPath}/${path}` : `${this.baseUrl}/${this.apiPath}/${path}/${data._id}`;
    if(query) {
      let components:any[] = []
      for (const k in query) {
        if (query.hasOwnProperty(k) && query[k] !== undefined && query[k] !== null)
          components.push( k + "=" + encodeURIComponent(query[k]) );
      }
      if(components.length) {
        url += `?${components.join('&')}`
      }
    }
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.put(url, data, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }

  async del<Type extends DbDoc>(collection:String, id:string ):Promise<DocReply<Type>> {
    const token = localStorage.token;
    const url = `${this.baseUrl}/${this.apiPath}/${collection}/${id}`;
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.delete(url, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }
  async download(path:String, data:any, mimeType:string ): Promise<GenericReply> {
    const token = localStorage.token;
    const url = `${this.baseUrl}/${this.apiPath}/${path}`;
    const config:AxiosRequestConfig = token ? {headers: {'x-token': `${token}`}, responseType: 'arraybuffer'} : {};

    try {
      let res = await axios.post(url, data, config);
      let blob = new Blob([res.data], { type: mimeType });
      const objUrl = window.URL.createObjectURL(blob)
      window.open(objUrl);
      return { success : true, status : 200 }
    } catch (e) {
      return this.handleError(e);
    }
  }

  async save<Type extends DbDoc>(path:String, data:Type):Promise<DocReply<Type>> {
    if(data._id) {
      return this.put(path, data)
    }
    return this.post(path, data)
  }
  async resetPassword(email:string):Promise<DocReply<any>> {
    const token = localStorage.token;
    const url = `${this.baseUrl}/reset`;
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.post(url, {email}, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }
  async getSeatsStats(form:string, to:string):Promise<StatsReply> {
    const token = localStorage.token;
    const url = `${this.baseUrl}/${this.apiPath}/stats/userseats?from=${form}&to=${to}`;
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.get(url, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }
  async getExpiringSeats():Promise<StatsReply> {
    const token = localStorage.token;
    const url = `${this.baseUrl}/${this.apiPath}/stats/expirations`;
    const config = token ? {headers: {'x-token': `${token}`}} : {};
    try {
      let res = await axios.get(url, config);
      return this.handleReply(res.data);
    } catch (e) {
      return this.handleError(e);
    }
  }
}


const api = new Api({apiServer:config.apiServer})

export default api
export { Api }
